import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BaseApiService } from './base-api.service';
import { ApiConfigService } from './api-config.service';
import {
    ArticleModel,
    ArticleModelPagedResponseModel,
    ArticleImportModel,
    ArticleSystemModel,
    ArticleSectionalPropertiesModel,
    QueryParams,
    SystemModel
} from '../models';
import { ApiResponse, ErrorResponseModel } from '../interfaces/base.interface';

/**
 * Articles API Service
 */
@Injectable({
    providedIn: 'root'
})
export class ArticlesService extends BaseApiService {
    constructor(
        http: HttpClient,
        apiConfig: ApiConfigService
    ) {
        super(http, apiConfig);
    }

    /**
     * Get all articles with pagination
     */
    getArticles(params?: QueryParams): Observable<ArticleModelPagedResponseModel> {
        return this.get<ArticleModelPagedResponseModel>('/articles', params)
            .pipe(map(response => response.data!));
    }

    /**
     * Get article by ID
     */
    getArticleById(id: string): Observable<ArticleModel> {
        return this.get<ArticleModel>(`/articles/${id}`)
            .pipe(map(response => response.data!));
    }

    /**
     * Get article by Name
     */
    getArticleByName(name: string): Observable<ArticleModel> {
        return this.get<ArticleModel>(`/articles/by-name/${name}`)
            .pipe(map(response => response.data!));
    }

    /**
     * Get sectional properties for articles
     */
    getArticleSectionalProperties(): Observable<ArticleSectionalPropertiesModel> {
        return this.get<ArticleSectionalPropertiesModel>('/articles/sectional-properties')
            .pipe(map(response => response.data!));
    }

    /**
     * Get articles by system
     * Note: This endpoint uses POST with SystemModel in body and pagination in query params
     */
    getArticlesBySystem(system: SystemModel | { name: string }, params?: QueryParams): Observable<ArticleModelPagedResponseModel> {
        // The endpoint expects a POST with SystemModel in body and query params for pagination
        // We need to use HttpClient directly since BaseApiService.post doesn't support query params
        const http = (this as any).http as HttpClient;
        const apiConfig = (this as any).apiConfig as ApiConfigService;
        
        const url = apiConfig.getApiUrl('/articles/by-system');
        let httpParams = new HttpParams();
        
        if (params) {
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (value !== null && value !== undefined && key !== 'systemName') {
                    httpParams = httpParams.set(key, value.toString());
                }
            });
        }
        
        return http.post<ArticleModelPagedResponseModel | ArticleModel[]>(url, system, { 
            params: httpParams,
            headers: apiConfig.getDefaultHeaders()
        })
            .pipe(
                map((response: ArticleModelPagedResponseModel | ArticleModel[]) => {
                    // Handle both wrapped ApiResponse and direct response
                    if (response && typeof response === 'object' && 'data' in response) {
                        return (response as any).data;
                    }
                    // If response is an array, wrap it in a paged response model
                    if (Array.isArray(response)) {
                        return {
                            data: response,
                            totalCount: response.length,
                            page: params?.page || 1,
                            pageSize: params?.pageSize || response.length,
                            totalPages: 1,
                            pagination: {
                                page: params?.page || 1,
                                pageSize: params?.pageSize || response.length,
                                totalItems: response.length,
                                totalPages: 1
                            }
                        } as ArticleModelPagedResponseModel;
                    }
                    return response as ArticleModelPagedResponseModel;
                }),
                catchError((error: HttpErrorResponse) => {
                    let errorResponse: ApiResponse<ArticleModelPagedResponseModel> = {
                        success: false,
                        error: {
                            Error: {
                                Code: error.status.toString(),
                                Message: error.message || 'An unexpected error occurred',
                                Details: [],
                                Timestamp: new Date()
                            }
                        }
                    };

                    if (error.error) {
                        try {
                            const apiError = error.error as ErrorResponseModel;
                            errorResponse.error = apiError;
                        } catch (e) {
                            // If parsing fails, use the default error structure
                        }
                    }

                    return throwError(() => errorResponse);
                })
            );
    }

    /**
     * Get article by system name and article name
     */
    getArticleBySystemAndName(systemName: string, articleName: string): Observable<ArticleModel> {
        return this.get<ArticleModel>(`/articles/system/${systemName}/${articleName}`)
            .pipe(map(response => response.data!));
    }

    /**
     * Import articles
     */
    importArticles(importData: ArticleImportModel): Observable<any> {
        return this.post<any>('/articles/import', importData)
            .pipe(map(response => response.data!));
    }

    /**
     * Create new article
     */
    createArticle(article: ArticleModel): Observable<ArticleModel> {
        return this.post<ArticleModel>('/articles', article)
            .pipe(map(response => response.data!));
    }

    /**
     * Update article
     */
    updateArticle(id: string, article: ArticleModel): Observable<ArticleModel> {
        return this.put<ArticleModel>(`/articles/${id}`, article)
            .pipe(map(response => response.data!));
    }

    /**
     * Delete article
     */
    deleteArticle(id: string): Observable<void> {
        return this.delete<void>(`/articles/${id}`)
            .pipe(map(() => undefined));
    }
}
